import { useFlagsFromDualInstances } from '@linktr.ee/feature-flags-mfe/DualStatsigInstances';
import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  applyLocalizationMode,
  LOCALIZATION_GATE_ID,
  type LocalizationMode,
} from '@shared/i18n';
import { reportClientError } from '@shared/utils/reportClientError';

const STATSIG_FALLBACK_TIMEOUT_MS = 1500;

export const useLocalizationModeSync = (): void => {
  const { getFlagFromWarehouseNativeWithExposure, initialised } =
    useFlagsFromDualInstances();
  const hasStatsigResolvedRef = useRef(initialised);
  hasStatsigResolvedRef.current = initialised;

  useLayoutEffect(() => {
    if (initialised) {
      return;
    }

    // StatsigDualInstanceProvider renders children even when init fails. Keep a
    // short grace period so healthy startups can resolve the gate first, but
    // fall back to control localization if Statsig hangs or fails so non-English
    // control users do not stay stuck on English forever. Re-check the latest
    // Statsig state inside the timer callback so a just-resolved experiment path
    // does not get overwritten by a stale control fallback.
    const fallbackTimer = window.setTimeout(() => {
      if (hasStatsigResolvedRef.current) {
        return;
      }

      void applyLocalizationMode('control').catch(error => {
        reportClientError('Failed to apply control localization mode', error);
      });
    }, STATSIG_FALLBACK_TIMEOUT_MS);

    return () => {
      window.clearTimeout(fallbackTimer);
    };
  }, [initialised]);

  useEffect(() => {
    if (!initialised) {
      return;
    }

    const isLocalizationEnabled = Boolean(
      getFlagFromWarehouseNativeWithExposure(LOCALIZATION_GATE_ID)
    );
    const mode: LocalizationMode = isLocalizationEnabled
      ? 'experiment'
      : 'control';

    void applyLocalizationMode(mode).catch(error => {
      reportClientError(`Failed to apply ${mode} localization mode`, error);
    });
  }, [getFlagFromWarehouseNativeWithExposure, initialised]);
};
